/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type ReactElement,
    type ReactNode,
    type PropsWithChildren,
    type RefObject,
    createContext,
    useContext,
    useRef,
    useEffect,
    useCallback,
    useState,
    lazy,
    Suspense,
} from 'react';
import { Await, useFetcher } from 'react-router';
import { Link } from '@/components/link';

export interface PromoMessage {
    id: string;
    message: string;
    linkUrl?: string;
    couponCode?: string;
}

// RGBARRY-CUSTOMIZATION: persists applied state in sessionStorage; treats CONFLICT (already in basket) as success
function CouponButton({ couponCode }: { couponCode: string }) {
    const storageKey = `promo-applied-${couponCode}`;
    const fetcher = useFetcher({ key: `promo-coupon-${couponCode}` });

    // Read persisted state on first render — if already applied this session, skip the button entirely
    const [status, setStatus] = useState<'idle' | 'applied' | 'hidden'>(() => {
        if (typeof window === 'undefined') return 'idle';
        return sessionStorage.getItem(storageKey) === 'applied' ? 'hidden' : 'idle';
    });

    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        const errorCode = (fetcher.data as { error?: { code?: string } })?.error?.code;
        // Success, already-in-basket (CONFLICT), or server error — all hide the button.
        // An invalid/expired code (INVALID_INPUT, EXPIRED) keeps it visible so the user knows it failed.
        const isAppliedOrUncertain = !errorCode || errorCode === 'CONFLICT' || errorCode === 'OPERATION_FAILED';
        if (isAppliedOrUncertain) {
            sessionStorage.setItem(storageKey, 'applied');
            setStatus('applied');
            const timer = setTimeout(() => setStatus('hidden'), 2000);
            return () => clearTimeout(timer);
        }
    }, [fetcher.state, fetcher.data, storageKey]);

    if (status === 'hidden') return null;
    if (status === 'applied') return <span className="ml-2 text-[10px] font-medium opacity-80">Applied ✓</span>;

    return (
        <fetcher.Form method="post" action="/action/promo-code-add" className="inline ml-2">
            <input type="hidden" name="promoCode" value={couponCode} />
            <button
                type="submit"
                disabled={fetcher.state !== 'idle'}
                className="text-[10px] font-medium px-2 py-0.5 bg-[var(--promo-bar-foreground)] text-[var(--promo-bar-background)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed leading-4">
                Tap to Apply
            </button>
        </fetcher.Form>
    );
}

interface HeaderScrollContextValue {
    scrolled: boolean;
    headerRef: RefObject<HTMLElement | null>;
    searchOpen: boolean;
}
const HeaderScrollContext = createContext<HeaderScrollContextValue>({
    scrolled: false,
    headerRef: { current: null },
    searchOpen: false,
});
// eslint-disable-next-line react-refresh/only-export-components
export function useHeaderScroll() {
    return useContext(HeaderScrollContext);
}
const SearchBar = lazy(() => import('./search'));
import CartBadge from './cart-badge';
import UserActions from './user-actions/user-actions';
import WishlistIcon from './wishlist-icon';
import { useTranslation } from 'react-i18next';
import logo from '/images/logo.svg';
import { Button } from '@/components/ui/button';
import { SparklesIcon } from '@/components/icons';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { openAgentWidget, isCimulateEnabled, validateCimulateConfig } from '@/components/cimulate';
import { UITarget } from '@/targets/ui-target';
import { Component } from '@/lib/decorators/component';
import { RegionDefinition } from '@/lib/decorators';
import { cn } from '@/lib/utils';

@Component('header', {
    name: 'Header',
    group: 'Layout',
    description: 'Global site header with navigation, search, and cart',
    embedded: true,
    component_id: 'header',
})
@RegionDefinition([{ id: 'announcement', name: 'Announcement', description: 'Displayed above the header' }])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class HeaderMetadata {}

interface HeaderProps extends PropsWithChildren {
    beforeHeader?: ReactNode;
    promoMessages?: Promise<PromoMessage[]> | PromoMessage[];
    /** Shown immediately while `promoMessages` is still streaming — prevents blank bar on first paint. */
    defaultPromoMessages?: PromoMessage[];
    variant?: 'full' | 'checkout';
}

// RGBARRY-CUSTOMIZATION: brand header rewrite — PD-driven promo bar, floating-pill on scroll,
// inline search drop-down, mobile-centered logo. Chat wired to the cimulate agent.

/** Inner promo bar — receives already-resolved messages, handles rotation */
function PromoBarInner({ messages }: { messages: PromoMessage[] }) {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (messages.length <= 1) return;
        const id = setInterval(() => setIndex((i) => (i + 1) % messages.length), 8000);
        return () => clearInterval(id);
    }, [messages.length]);

    const prev = () => setIndex((i) => (i - 1 + messages.length) % messages.length);
    const next = () => setIndex((i) => (i + 1) % messages.length);

    const msg = messages[index % messages.length];
    if (!msg) return null;

    return (
        <div className="relative bg-[var(--promo-bar-background)] text-[var(--promo-bar-foreground)] text-xs tracking-[0.12em] h-9 flex items-center justify-center">
            {messages.length > 1 && (
                <button
                    onClick={prev}
                    aria-label="Previous message"
                    className="absolute left-4 lg:left-16 opacity-50 hover:opacity-100 transition-opacity text-base leading-none cursor-pointer">
                    ‹
                </button>
            )}
            <span className="text-center px-12 inline-flex items-center gap-1">
                {msg.linkUrl ? (
                    <Link to={msg.linkUrl} className="hover:underline">
                        {msg.message}
                    </Link>
                ) : (
                    msg.message
                )}
                {msg.couponCode && <CouponButton key={msg.id} couponCode={msg.couponCode} />}
            </span>
            {messages.length > 1 && (
                <button
                    onClick={next}
                    aria-label="Next message"
                    className="absolute right-4 lg:right-16 opacity-50 hover:opacity-100 transition-opacity text-base leading-none cursor-pointer">
                    ›
                </button>
            )}
        </div>
    );
}

/**
 * Promo bar — always full-width at top, never floats.
 *
 * Accepts a streamed promise so it never blocks page render. `defaultMessages`
 * renders immediately on first paint (no network wait, no CLS) and is replaced
 * by the full PD-driven list once the promise resolves. No extra fetch — chains
 * off the header component promise that _app.tsx already fires for design mode.
 */
function PromoBar({
    messages,
    defaultMessages,
}: {
    messages: Promise<PromoMessage[]> | PromoMessage[] | undefined;
    defaultMessages?: PromoMessage[];
}) {
    if (!messages) {
        return defaultMessages?.length ? <PromoBarInner messages={defaultMessages} /> : null;
    }

    if (!(messages instanceof Promise)) {
        return <PromoBarInner messages={messages} />;
    }

    const fallback = defaultMessages?.length ? (
        <PromoBarInner messages={defaultMessages} />
    ) : (
        <div
            aria-hidden="true"
            className="h-9 bg-[var(--promo-bar-background)] animate-pulse flex items-center justify-center">
            <div className="h-3 w-48 rounded bg-[var(--promo-bar-foreground)]/20" />
        </div>
    );

    return (
        <Suspense fallback={fallback}>
            <Await
                resolve={messages}
                errorElement={defaultMessages?.length ? <PromoBarInner messages={defaultMessages} /> : null}>
                {(resolved) => <PromoBarInner messages={resolved.length ? resolved : (defaultMessages ?? [])} />}
            </Await>
        </Suspense>
    );
}

export default function Header({
    children,
    beforeHeader,
    promoMessages,
    defaultPromoMessages,
    variant = 'full',
}: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    // wrapperRef covers the entire chrome block (promo bar + header)
    // so --header-height matches what content should offset from
    const wrapperRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const config = useConfig();
    const [scrolled, setScrolled] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    // Close search panel on outside click
    useEffect(() => {
        if (!searchOpen) return;
        const handler = (e: MouseEvent) => {
            if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
                setSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [searchOpen]);

    const showChat =
        variant === 'full' &&
        isCimulateEnabled(config.cimulateAgent?.enabled) &&
        validateCimulateConfig(config.cimulateAgent);

    // RGBARRY-CUSTOMIZATION: floating-pill scroll listener — pill kicks in at 80px; close search on scroll
    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 80);
            setSearchOpen(false);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        setScrolled(window.scrollY > 80);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Keep --header-height accurate for mega-menu positioning
    const updateHeaderHeight = useCallback(() => {
        if (wrapperRef.current) {
            const height = `${wrapperRef.current.offsetHeight}px`;
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    if (variant === 'checkout') {
        return (
            <div ref={wrapperRef}>
                <header ref={headerRef} className="bg-background border-b border-[#e8e4e0] sticky top-0 z-50">
                    <div className="section-container">
                        <div className="flex items-center h-16">
                            <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                                <img src={logo} alt={t('logoAlt')} className="h-7 w-auto" />
                            </Link>
                            <div className="flex-1" />
                            <CartBadge />
                        </div>
                    </div>
                </header>
            </div>
        );
    }

    return (
        <HeaderScrollContext value={{ scrolled, headerRef, searchOpen }}>
            {/* wrapperRef div is always in the document flow to measure total chrome height.
            When scrolled, the inner pill becomes fixed and we inject a spacer here instead. */}
            <div ref={wrapperRef}>
                {/* RGBARRY-CUSTOMIZATION: PD-driven promo bar — streams off existing headerComponent fetch, no extra request */}
                <PromoBar messages={promoMessages} defaultMessages={defaultPromoMessages} />
                {beforeHeader && <div className="flex justify-end section-container">{beforeHeader}</div>}

                {/* Spacer replaces the header height when it goes fixed */}
                {scrolled && <div className="h-16" aria-hidden="true" />}

                {/* Floating pill shell */}
                <div
                    className={cn(
                        'transition-all duration-300 ease-out',
                        scrolled ? 'fixed top-3 left-0 right-0 px-4 lg:px-8 z-50' : 'relative z-40'
                    )}>
                    <header
                        ref={headerRef}
                        className={cn(
                            'transition-all duration-300 ease-out',
                            scrolled
                                ? 'bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-white/60 max-w-[1200px] mx-auto'
                                : 'bg-white border-b border-[#e8e4e0]'
                        )}>
                        {/* Main header row */}
                        <div
                            className={cn(
                                'flex items-center transition-all duration-300',
                                scrolled ? 'px-5 h-14' : 'pl-3 pr-6 lg:pl-4 lg:pr-8 h-16 max-w-[1280px] mx-auto'
                            )}>
                            {/* Logo — desktop only, left-anchored */}
                            <Link
                                to="/"
                                className="hidden lg:flex items-center shrink-0 mr-4 lg:mr-6"
                                data-testid="header-logo">
                                <img
                                    src={logo}
                                    alt={t('logoAlt')}
                                    className={cn('w-auto transition-all duration-300', scrolled ? 'h-6' : 'h-7')}
                                />
                            </Link>

                            {/* Mobile: left-side actions (hamburger + search) — hidden on desktop */}
                            <div className="flex items-center lg:hidden">
                                {children}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setSearchOpen((o) => !o)}
                                    className="hover:bg-transparent hover:opacity-50 transition-opacity"
                                    aria-label={searchOpen ? 'Close search' : 'Open search'}
                                    aria-expanded={searchOpen}>
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.75"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="m21 21-4.35-4.35" />
                                    </svg>
                                </Button>
                            </div>

                            {/* Mobile: centered logo — absolute so it doesn't affect flex layout */}
                            <Link
                                to="/"
                                className="lg:hidden absolute left-1/2 -translate-x-1/2 flex items-center shrink-0"
                                data-testid="header-logo-mobile">
                                <img src={logo} alt={t('logoAlt')} className="h-6 w-auto" />
                            </Link>

                            {/* Mobile: right-side cart */}
                            <div className="flex items-center lg:hidden ml-auto">
                                <CartBadge />
                            </div>

                            {/* Desktop: spacer pushes nav+icons to the right */}
                            <div className="hidden lg:flex flex-1" />

                            {/* Desktop: nav + divider + icons — all right-aligned */}
                            <div className="hidden lg:flex items-center">
                                {children}
                                <div className="w-px h-5 bg-[#e8e4e0] mx-2" />
                                <button
                                    onClick={() => setSearchOpen((o) => !o)}
                                    className={cn(
                                        'flex items-center gap-1.5 h-9 px-3 text-[12px] tracking-wide transition-colors cursor-pointer rounded-none',
                                        searchOpen
                                            ? 'text-[#1a1a1a] bg-[#f5f5f5]'
                                            : 'text-[#6b6b6b] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'
                                    )}
                                    aria-label={searchOpen ? 'Close search' : 'Open search'}
                                    aria-expanded={searchOpen}>
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.75"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="m21 21-4.35-4.35" />
                                    </svg>
                                    {!scrolled && <span>Search</span>}
                                </button>
                                <UITarget targetId="sfcc.header.before.cart" />
                                {showChat && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9 text-[#6b6b6b] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] hover:opacity-100 transition-colors"
                                        onClick={() => openAgentWidget()}
                                        aria-label={t('openChat')}>
                                        <SparklesIcon />
                                    </Button>
                                )}
                                <UserActions />
                                <WishlistIcon />
                                <CartBadge />
                            </div>
                        </div>

                        {/* Search drop-down — full-width strip below nav row */}
                        {searchOpen && (
                            <div className="border-t border-[#e8e4e0] px-4 lg:px-8 py-3 [&_input]:h-10 [&_input]:text-sm [&_input]:border-[#e8e4e0] [&_input]:rounded-none [&_input]:bg-[#f9f9f9] [&_input]:text-[#1a1a1a] [&_input]:placeholder:text-[#9a9a9a] [&_input]:focus-visible:ring-1 [&_input]:focus-visible:ring-[#1a1a1a] [&_input]:focus-visible:border-[#1a1a1a] [&_svg]:text-[#9a9a9a]">
                                {/* RGBARRY-CUSTOMIZATION: search strip with close button */}
                                <div className="max-w-[640px] mx-auto flex items-center gap-3">
                                    <div className="flex-1 [&_svg]:text-[#9a9a9a]">
                                        <Suspense fallback={null}>
                                            <SearchBar />
                                        </Suspense>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setSearchOpen(false)}
                                        aria-label="Close search"
                                        className="group shrink-0 flex items-center justify-center w-8 h-8 text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.75"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="transition-transform duration-300 group-hover:rotate-90">
                                            <path d="M18 6 6 18" />
                                            <path d="m6 6 12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}

                        <UITarget targetId="sfcc.header.bnpl.banner" />
                    </header>
                </div>
            </div>
        </HeaderScrollContext>
    );
}
